// src/components/forms/AttendanceForm.jsx - With Notes Modal
import React, { useState, useEffect } from 'react';
import { 
  Users, 
  CheckCircle, 
  AlertCircle,
  Calendar,
  Clock,
  BookOpen,
  School,
  Briefcase,
  Save,
  X,
  Loader2,
  User,
  Target,
  MapPin,
  UserCheck,
  UserX,
  Clock as ClockIcon,
  FileText,
  Edit3,
  MessageSquare
} from 'lucide-react';
import { AttendanceService } from '../../services/attendanceService';
import { SessionService } from '../../services/sessionService';
import { useAuth } from '../../contexts/AuthContext';

// Notes Modal Component
const NotesModal = ({ isOpen, onClose, learner, notes, onSave }) => {
  const [noteText, setNoteText] = useState(notes || '');

  useEffect(() => {
    setNoteText(notes || '');
  }, [notes, isOpen]);

  const handleSave = () => {
    onSave(noteText);
    onClose();
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Escape') {
      onClose();
    }
    if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
      handleSave();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
        {/* Modal Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <div className="flex items-center gap-3">
            <MessageSquare className="w-5 h-5 text-blue-600" />
            <div>
              <h3 className="text-lg font-semibold text-gray-900">Notes</h3>
              <p className="text-sm text-gray-500">
                {learner?.learnerName} {learner?.learnerSurname}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-1 text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Modal Body */}
        <div className="p-4">
          <textarea
            value={noteText}
            onChange={(e) => setNoteText(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Add notes about attendance, behavior, participation, or any other observations..."
            rows={6}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
            autoFocus
          />
          <p className="text-xs text-gray-500 mt-2">
            Press Ctrl+Enter to save quickly, or Escape to cancel
          </p>
        </div>

        {/* Modal Footer */}
        <div className="flex items-center justify-end gap-3 p-4 border-t border-gray-200">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSave}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            <Save className="w-4 h-4" />
            Save Notes
          </button>
        </div>
      </div>
    </div>
  );
};

const AttendanceForm = ({ sessionId, onSubmit, onCancel }) => {
  const { userProfile, userContexts } = useAuth();
  const [loading, setLoading] = useState(false);
  const [loadingSession, setLoadingSession] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [session, setSession] = useState(null);
  const [attendanceData, setAttendanceData] = useState([]);

  // Notes modal state
  const [notesModal, setNotesModal] = useState({
    isOpen: false,
    learnerIndex: null,
    learner: null,
    currentNotes: ''
  });

  // Quick action buttons for bulk operations
  const [showQuickActions, setShowQuickActions] = useState(true);

  useEffect(() => {
    if (sessionId) {
      loadSession();
    }
  }, [sessionId]);

  const loadSession = async () => {
    try {
      setLoadingSession(true);
      setError(null);
      
      const sessionData = await SessionService.getSession(sessionId);
      console.log('Loaded session data:', sessionData);
      setSession(sessionData);
      
      let sessionLearners = [];
      
      if (sessionData.sessionLearners && sessionData.sessionLearners.length > 0) {
        sessionLearners = sessionData.sessionLearners;
      } else if (sessionData.context && sessionData.grades && sessionData.subject) {
        // Use context info that was saved with the session
        sessionLearners = await SessionService.getSessionLearners(
          sessionData.context.name,  // "Sizathina High School"
          sessionData.context.type,  // "institution"  
          sessionData.grades,        // ["10"]
          sessionData.subject        // "Mathematics"
        );
      }
      
      // Initialize attendance data
      const initialAttendance = sessionLearners.map(learner => ({
        id: learner.id,
        learnerSurname: learner.learnerSurname,
        learnerName: learner.learnerName,
        grade: learner.grade,
        school: learner.school,
        attendance: 'Present',
        engagement: 'H',
        behaviour: 'G',
        collaboration: 'A',
        notes: ''
      }));
      
      console.log('Initialized attendance data:', initialAttendance);
      setAttendanceData(initialAttendance);
      
    } catch (err) {
      console.error('Error loading session:', err);
      setError('Failed to load session details: ' + err.message);
    } finally {
      setLoadingSession(false);
    }
  };

  const handleAttendanceChange = (learnerIndex, field, value) => {
    setAttendanceData(prev => 
      prev.map((learner, index) => 
        index === learnerIndex 
          ? { ...learner, [field]: value }
          : learner
      )
    );
  };

  // Notes modal functions
  const openNotesModal = (learnerIndex) => {
    const learner = attendanceData[learnerIndex];
    setNotesModal({
      isOpen: true,
      learnerIndex: learnerIndex,
      learner: learner,
      currentNotes: learner.notes
    });
  };

  const closeNotesModal = () => {
    setNotesModal({
      isOpen: false,
      learnerIndex: null,
      learner: null,
      currentNotes: ''
    });
  };

  const saveNotes = (notes) => {
    if (notesModal.learnerIndex !== null) {
      handleAttendanceChange(notesModal.learnerIndex, 'notes', notes);
    }
  };

  // Quick action functions
  const markAllPresent = () => {
    setAttendanceData(prev => prev.map(learner => ({ ...learner, attendance: 'Present' })));
  };

  const markAllLate = () => {
    setAttendanceData(prev => prev.map(learner => ({ ...learner, attendance: 'Late' })));
  };

  const markAllAbsent = () => {
    setAttendanceData(prev => prev.map(learner => ({ ...learner, attendance: 'Absent' })));
  };

  const setAllEngagement = (level) => {
    setAttendanceData(prev => prev.map(learner => ({ ...learner, engagement: level })));
  };

  const setAllBehaviour = (level) => {
    setAttendanceData(prev => prev.map(learner => ({ ...learner, behaviour: level })));
  };

  const calculateStats = () => {
    const total = attendanceData.length;
    const present = attendanceData.filter(l => l.attendance === 'Present').length;
    const late = attendanceData.filter(l => l.attendance === 'Late').length;
    const absent = attendanceData.filter(l => l.attendance === 'Absent').length;
    const excused = attendanceData.filter(l => l.attendance === 'Excused').length;
    
    const attendanceRate = total > 0 ? Math.round((present + late) / total * 100) : 0;
    
    return { total, present, late, absent, excused, attendanceRate };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (attendanceData.length === 0) {
      setError('No learners found for attendance recording');
      return;
    }
    
    try {
      setLoading(true);
      setError(null);
      
      const stats = calculateStats();
      
      const attendanceRecord = {
        sessionId: session.id,
        sessionTitle: session.sessionTitle,
        sessionDate: session.sessionDate,
        subject: session.subject,
        grades: session.grades,
        contextId: session.contextId,
        recordedAt: new Date(),
        recordedBy: userProfile.email,
        attendanceData: attendanceData,
        stats: stats
      };
      
      console.log('Submitting attendance record:', attendanceRecord);
      
      // Save the attendance record
      await AttendanceService.createAttendanceRecord(attendanceRecord);
      
      setSuccess(true);
      setTimeout(() => {
        onSubmit && onSubmit();
      }, 1500);
      
    } catch (err) {
      console.error('Error saving attendance:', err);
      setError('Failed to save attendance: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const getAttendanceButtonStyle = (currentValue, buttonValue) => {
    const baseStyle = "px-3 py-1 text-xs font-medium rounded transition-colors ";
    if (currentValue === buttonValue) {
      switch (buttonValue) {
        case 'Present':
          return baseStyle + "bg-green-500 text-white";
        case 'Late':
          return baseStyle + "bg-yellow-500 text-white";
        case 'Absent':
          return baseStyle + "bg-red-500 text-white";
        case 'Excused':
          return baseStyle + "bg-blue-500 text-white";
        default:
          return baseStyle + "bg-gray-500 text-white";
      }
    }
    return baseStyle + "bg-gray-200 text-gray-700 hover:bg-gray-300";
  };

  const getEngagementButtonStyle = (currentValue, buttonValue) => {
    const baseStyle = "px-2 py-1 text-xs font-medium rounded transition-colors ";
    if (currentValue === buttonValue) {
      switch (buttonValue) {
        case 'H':
          return baseStyle + "bg-green-500 text-white";
        case 'M':
          return baseStyle + "bg-yellow-500 text-white";
        case 'L':
          return baseStyle + "bg-red-500 text-white";
        default:
          return baseStyle + "bg-gray-500 text-white";
      }
    }
    return baseStyle + "bg-gray-200 text-gray-700 hover:bg-gray-300";
  };

  const getBehaviourButtonStyle = (currentValue, buttonValue) => {
    const baseStyle = "px-2 py-1 text-xs font-medium rounded transition-colors ";
    if (currentValue === buttonValue) {
      switch (buttonValue) {
        case 'E':
          return baseStyle + "bg-green-500 text-white";
        case 'G':
          return baseStyle + "bg-blue-500 text-white";
        case 'S':
          return baseStyle + "bg-yellow-500 text-white";
        case 'N':
          return baseStyle + "bg-red-500 text-white";
        default:
          return baseStyle + "bg-gray-500 text-white";
      }
    }
    return baseStyle + "bg-gray-200 text-gray-700 hover:bg-gray-300";
  };

  const getSessionTypeIcon = (sessionContext) => {
    if (session?.context?.type === 'institution') {
      return <School className="w-5 h-5 text-blue-500" />;
    } else {
      return <Briefcase className="w-5 h-5 text-green-500" />;
    }
  };

  if (loadingSession) {
    return (
      <div className="max-w-4xl mx-auto p-4">
        <div className="bg-white rounded-lg shadow-lg p-6">
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            <span className="ml-2 text-gray-600">Loading session details...</span>
          </div>
        </div>
      </div>
    );
  }

  if (error && !session) {
    return (
      <div className="max-w-4xl mx-auto p-4">
        <div className="bg-white rounded-lg shadow-lg p-6">
          <div className="text-center py-12">
            <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">Error Loading Session</h3>
            <p className="text-gray-500 mb-6">{error}</p>
            <button
              onClick={onCancel}
              className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700"
            >
              Back to Sessions
            </button>
          </div>
        </div>
      </div>
    );
  }

  const context = userContexts?.find(ctx => ctx.id === session?.contextId);
  const stats = calculateStats();

  return (
    <>
      <div className="max-w-6xl mx-auto p-4">
        <div className="bg-white rounded-lg shadow-lg">
          {/* Header */}
          <div className="border-b border-gray-200 p-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Users className="w-6 h-6 text-blue-600" />
                <div>
                  <h2 className="text-2xl font-bold text-gray-900">Record Attendance</h2>
                  <p className="text-gray-600">Mark attendance for session learners</p>
                </div>
              </div>
              {onCancel && (
                <button
                  onClick={onCancel}
                  className="p-2 text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <X className="w-6 h-6" />
                </button>
              )}
            </div>
          </div>

          {/* Session Information */}
          {session && (
            <div className="p-6 bg-gray-50 border-b border-gray-200">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <div className="flex items-center gap-3">
                  <div className="flex items-center gap-2">
                    {getSessionTypeIcon()}
                    <span className="text-sm font-medium text-gray-900">{session.sessionTitle}</span>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <Calendar className="w-4 h-4 text-gray-400" />
                  <div className="text-sm text-gray-600">
                    {new Date(session.sessionDate).toLocaleDateString()}
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <BookOpen className="w-4 h-4 text-gray-400" />
                  <div className="text-sm text-gray-600">
                    {session.subject} - Grade {session.grades?.join(', ')}
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <MapPin className="w-4 h-4 text-gray-400" />
                  <div className="text-sm text-gray-600">
                    {context?.name || 'Context not found'}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Error Display */}
          {error && (
            <div className="p-6 border-b border-gray-200">
              <div className="bg-red-50 border border-red-200 rounded-md p-4 flex items-center gap-2">
                <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0" />
                <span className="text-red-700 text-sm">{error}</span>
              </div>
            </div>
          )}

          {/* Success Display */}
          {success && (
            <div className="p-6 border-b border-gray-200">
              <div className="bg-green-50 border border-green-200 rounded-md p-4 flex items-center gap-2">
                <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
                <span className="text-green-700 text-sm">Attendance recorded successfully!</span>
              </div>
            </div>
          )}

          {/* Quick Actions */}
          {attendanceData.length > 0 && (
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900">Quick Actions</h3>
                <button
                  onClick={() => setShowQuickActions(!showQuickActions)}
                  className="text-sm text-blue-600 hover:text-blue-800"
                >
                  {showQuickActions ? 'Hide' : 'Show'} Quick Actions
                </button>
              </div>
              
              {showQuickActions && (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {/* Attendance Quick Actions */}
                  <div>
                    <h4 className="text-sm font-medium text-gray-700 mb-2">Mark All Attendance</h4>
                    <div className="flex gap-2">
                      <button
                        type="button"
                        onClick={markAllPresent}
                        className="flex items-center gap-1 px-3 py-2 bg-green-100 text-green-700 rounded hover:bg-green-200 text-sm"
                      >
                        <UserCheck className="w-4 h-4" />
                        Present
                      </button>
                      <button
                        type="button"
                        onClick={markAllLate}
                        className="flex items-center gap-1 px-3 py-2 bg-yellow-100 text-yellow-700 rounded hover:bg-yellow-200 text-sm"
                      >
                        <ClockIcon className="w-4 h-4" />
                        Late
                      </button>
                      <button
                        type="button"
                        onClick={markAllAbsent}
                        className="flex items-center gap-1 px-3 py-2 bg-red-100 text-red-700 rounded hover:bg-red-200 text-sm"
                      >
                        <UserX className="w-4 h-4" />
                        Absent
                      </button>
                    </div>
                  </div>

                  {/* Engagement Quick Actions */}
                  <div>
                    <h4 className="text-sm font-medium text-gray-700 mb-2">Set All Engagement</h4>
                    <div className="flex gap-2">
                      <button
                        type="button"
                        onClick={() => setAllEngagement('H')}
                        className="px-3 py-2 bg-green-100 text-green-700 rounded hover:bg-green-200 text-sm"
                      >
                        High
                      </button>
                      <button
                        type="button"
                        onClick={() => setAllEngagement('M')}
                        className="px-3 py-2 bg-yellow-100 text-yellow-700 rounded hover:bg-yellow-200 text-sm"
                      >
                        Medium
                      </button>
                      <button
                        type="button"
                        onClick={() => setAllEngagement('L')}
                        className="px-3 py-2 bg-red-100 text-red-700 rounded hover:bg-red-200 text-sm"
                      >
                        Low
                      </button>
                    </div>
                  </div>

                  {/* Behaviour Quick Actions */}
                  <div>
                    <h4 className="text-sm font-medium text-gray-700 mb-2">Set All Behaviour</h4>
                    <div className="flex gap-1">
                      <button
                        type="button"
                        onClick={() => setAllBehaviour('E')}
                        className="px-2 py-2 bg-green-100 text-green-700 rounded hover:bg-green-200 text-xs"
                      >
                        Excellent
                      </button>
                      <button
                        type="button"
                        onClick={() => setAllBehaviour('G')}
                        className="px-2 py-2 bg-blue-100 text-blue-700 rounded hover:bg-blue-200 text-xs"
                      >
                        Good
                      </button>
                      <button
                        type="button"
                        onClick={() => setAllBehaviour('S')}
                        className="px-2 py-2 bg-yellow-100 text-yellow-700 rounded hover:bg-yellow-200 text-xs"
                      >
                        Satisfactory
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Attendance Statistics */}
          <div className="p-6 border-b border-gray-200">
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
              <div className="text-center">
                <div className="text-2xl font-bold text-gray-900">{stats.total}</div>
                <div className="text-sm text-gray-500">Total</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-green-600">{stats.present}</div>
                <div className="text-sm text-gray-500">Present</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-yellow-600">{stats.late}</div>
                <div className="text-sm text-gray-500">Late</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-red-600">{stats.absent}</div>
                <div className="text-sm text-gray-500">Absent</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-blue-600">{stats.attendanceRate}%</div>
                <div className="text-sm text-gray-500">Rate</div>
              </div>
            </div>
          </div>

          {/* Learners List with Notes Modal */}
          <form onSubmit={handleSubmit}>
            <div className="p-6">
              {attendanceData.length > 0 ? (
                <>
                  <div className="mb-4">
                    <h3 className="text-lg font-semibold text-gray-900">
                      Learners ({attendanceData.length})
                    </h3>
                    <p className="text-gray-600">Click buttons to quickly mark attendance and engagement</p>
                  </div>

                  <div className="space-y-3 max-h-96 overflow-y-auto">
                    {attendanceData.map((learner, index) => (
                      <div key={index} className="border border-gray-200 rounded-lg p-4">
                        <div className="grid grid-cols-1 lg:grid-cols-12 gap-3 items-center">
                          {/* Learner Info */}
                          <div className="lg:col-span-3">
                            <div className="flex items-center gap-3">
                              <User className="w-8 h-8 text-gray-400" />
                              <div>
                                <p className="font-medium text-gray-900 text-sm">
                                  {learner.learnerName} {learner.learnerSurname}
                                </p>
                                <p className="text-xs text-gray-500">
                                  Grade {learner.grade} • {learner.school}
                                </p>
                              </div>
                            </div>
                          </div>

                          {/* Attendance Buttons */}
                          <div className="lg:col-span-4">
                            <div className="flex gap-1">
                              <button
                                type="button"
                                onClick={() => handleAttendanceChange(index, 'attendance', 'Present')}
                                className={getAttendanceButtonStyle(learner.attendance, 'Present')}
                              >
                                Present
                              </button>
                              <button
                                type="button"
                                onClick={() => handleAttendanceChange(index, 'attendance', 'Late')}
                                className={getAttendanceButtonStyle(learner.attendance, 'Late')}
                              >
                                Late
                              </button>
                              <button
                                type="button"
                                onClick={() => handleAttendanceChange(index, 'attendance', 'Absent')}
                                className={getAttendanceButtonStyle(learner.attendance, 'Absent')}
                              >
                                Absent
                              </button>
                              <button
                                type="button"
                                onClick={() => handleAttendanceChange(index, 'attendance', 'Excused')}
                                className={getAttendanceButtonStyle(learner.attendance, 'Excused')}
                              >
                                Excused
                              </button>
                            </div>
                          </div>

                          {/* Engagement Buttons */}
                          <div className="lg:col-span-2">
                            <div className="flex gap-1">
                              <button
                                type="button"
                                onClick={() => handleAttendanceChange(index, 'engagement', 'H')}
                                className={getEngagementButtonStyle(learner.engagement, 'H')}
                              >
                                H
                              </button>
                              <button
                                type="button"
                                onClick={() => handleAttendanceChange(index, 'engagement', 'M')}
                                className={getEngagementButtonStyle(learner.engagement, 'M')}
                              >
                                M
                              </button>
                              <button
                                type="button"
                                onClick={() => handleAttendanceChange(index, 'engagement', 'L')}
                                className={getEngagementButtonStyle(learner.engagement, 'L')}
                              >
                                L
                              </button>
                            </div>
                          </div>

                          {/* Behaviour Buttons */}
                          <div className="lg:col-span-2">
                            <div className="flex gap-1">
                              <button
                                type="button"
                                onClick={() => handleAttendanceChange(index, 'behaviour', 'E')}
                                className={getBehaviourButtonStyle(learner.behaviour, 'E')}
                              >
                                E
                              </button>
                              <button
                                type="button"
                                onClick={() => handleAttendanceChange(index, 'behaviour', 'G')}
                                className={getBehaviourButtonStyle(learner.behaviour, 'G')}
                              >
                                G
                              </button>
                              <button
                                type="button"
                                onClick={() => handleAttendanceChange(index, 'behaviour', 'S')}
                                className={getBehaviourButtonStyle(learner.behaviour, 'S')}
                              >
                                S
                              </button>
                              <button
                                type="button"
                                onClick={() => handleAttendanceChange(index, 'behaviour', 'N')}
                                className={getBehaviourButtonStyle(learner.behaviour, 'N')}
                              >
                                N
                              </button>
                            </div>
                          </div>

                          {/* Notes Button */}
                          <div className="lg:col-span-1">
                            <button
                              type="button"
                              onClick={() => openNotesModal(index)}
                              className={`w-full flex items-center justify-center gap-1 px-2 py-2 rounded text-xs font-medium transition-colors ${
                                learner.notes 
                                  ? 'bg-blue-100 text-blue-700 hover:bg-blue-200' 
                                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                              }`}
                              title={learner.notes ? 'Edit notes' : 'Add notes'}
                            >
                              {learner.notes ? (
                                <>
                                  <FileText className="w-3 h-3" />
                                  <span className="hidden sm:inline">Notes</span>
                                </>
                              ) : (
                                <>
                                  <Edit3 className="w-3 h-3" />
                                  <span className="hidden sm:inline">Add</span>
                                </>
                              )}
                            </button>
                          </div>
                        </div>

                        {/* Notes Preview */}
                        {learner.notes && (
                          <div className="mt-3 p-2 bg-gray-50 rounded text-xs text-gray-600 border-l-2 border-blue-200">
                            <p className="line-clamp-2">{learner.notes}</p>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </>
              ) : (
                <div className="text-center py-12">
                  <Users className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">No Learners Found</h3>
                  <p className="text-gray-500">
                    This session doesn't have any learners assigned. Please check the session configuration.
                  </p>
                </div>
              )}
            </div>

            {/* Form Actions */}
            {attendanceData.length > 0 && (
              <div className="border-t border-gray-200 px-6 py-4">
                <div className="flex justify-end gap-3">
                  {onCancel && (
                    <button
                      type="button"
                      onClick={onCancel}
                      className="px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors"
                    >
                      Cancel
                    </button>
                  )}
                  <button
                    type="submit"
                    disabled={loading}
                    className="flex items-center gap-2 px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    {loading ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" />
                        Recording Attendance...
                      </>
                    ) : (
                      <>
                        <Save className="w-4 h-4" />
                        Record Attendance
                      </>
                    )}
                  </button>
                </div>
              </div>
            )}
          </form>
        </div>
      </div>

      {/* Notes Modal */}
      <NotesModal
        isOpen={notesModal.isOpen}
        onClose={closeNotesModal}
        learner={notesModal.learner}
        notes={notesModal.currentNotes}
        onSave={saveNotes}
      />
    </>
  );
};

export default AttendanceForm;